import { useRef, useState, useEffect } from 'react';
import { useInView } from 'framer-motion';

/* ── Animated stat counter with staggered reveal ── */
function AnimatedStat({ target, decimals = 0, suffix = '', color, label, delay, isDark = false }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (!isInView) return;
    const timer = setTimeout(() => {
      if (target === 0) {
        setValue(0);
        return;
      }
      const duration = 2000;
      const start = performance.now();
      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        setValue(eased * target);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay);
    return () => clearTimeout(timer);
  }, [isInView, target, delay]);

  const display =
    value === null
      ? '\u00A0'
      : decimals > 0
      ? Number(value).toFixed(decimals)
      : Math.round(value);

  return (
    <div
      ref={ref}
      className="mb-10"
      style={{ opacity: value !== null ? 1 : 0.12, transition: 'opacity 0.6s ease' }}
    >
      <p
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '48px',
          fontWeight: 300,
          color,
          lineHeight: 1,
        }}
      >
        {display}
        {value !== null ? suffix : ''}
      </p>
      <p
        className="mt-3"
        style={{ fontSize: '12px', color: isDark ? 'rgba(240,235,227,0.5)' : '#8C7E6E', lineHeight: 1.5 }}
      >
        {label}
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════
   SECTION 2 — THE SHIFT
   Gallery Wall warm background variant.
   ══════════════════════════════════════════ */
export default function TheShift({ variant = 'warm' }) {
  const isDark = variant === 'dark';
  return (
    <section className="py-24 md:py-32 px-6 md:px-10" data-testid="the-shift-section">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-12 md:gap-16">
        {/* Left — 60 % */}
        <div className="md:w-[60%]">
          <p
            className="mb-6"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '11px',
              color: '#B8985A',
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              fontWeight: 500,
            }}
            data-testid="shift-eyebrow"
          >
            THE SHIFT IS ALREADY HAPPENING
          </p>

          <h2
            className="mb-8"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(28px, 3.5vw, 36px)',
              fontWeight: 300,
              color: isDark ? '#F0EBE3' : '#3C3226',
              lineHeight: 1.3,
            }}
            data-testid="shift-headline"
          >
            While you were working, AI started choosing who gets your customers.
          </h2>

          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 300,
              color: isDark ? 'rgba(240,235,227,0.6)' : '#5C5347',
              maxWidth: '480px',
              lineHeight: 1.8,
            }}
            data-testid="shift-body"
          >
            Right now, when someone in your city asks ChatGPT, Gemini, or
            Perplexity for a recommendation, AI picks the answer. Not Google.
            Not Yelp. Not word of mouth. AI. And if your business isn't
            optimized for how AI thinks, you're invisible to a growing majority
            of your potential customers.
          </p>
        </div>

        {/* Right — 40 % */}
        <div className="md:w-[40%] flex flex-col justify-center">
          <AnimatedStat
            target={73}
            suffix="%"
            color="#B8985A"
            label="of consumers now use AI to find local services"
            delay={0}
            isDark={isDark}
          />
          <AnimatedStat
            target={4.2}
            decimals={1}
            suffix="B"
            color="#B8985A"
            label="AI search queries per month in 2026"
            delay={400}
            isDark={isDark}
          />
          <AnimatedStat
            target={0}
            suffix="%"
            color="#C67A5C"
            label="of your competitors are optimized for it"
            delay={800}
            isDark={isDark}
          />
          <p
            className="text-right"
            style={{ fontSize: '10px', color: isDark ? 'rgba(240,235,227,0.3)' : '#AAAAAA' }}
            data-testid="shift-source"
          >
            Source: HubSpot State of AI Marketing 2026
          </p>
        </div>
      </div>
    </section>
  );
}
